import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useOrg } from '../../hooks/useOrg';
import { supabase } from '../../lib/supabase';

export default function ClientProfile() {
  const { profile, fetchProfile, user } = useAuth();
  const { currentOrg } = useOrg();
  const [form, setForm] = useState({
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    phone: profile?.phone || '',
    sms_opt_in: profile?.sms_opt_in || false,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);

    // Consent timestamps: only the patient's own toggle writes these —
    // they're the TCPA record of when this number opted in or out.
    const smsChanged = form.sms_opt_in !== !!profile?.sms_opt_in;
    const { error } = await supabase
      .from('users')
      .update({
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone || null,
        sms_opt_in: form.sms_opt_in,
        ...(smsChanged && form.sms_opt_in && { sms_opt_in_at: new Date().toISOString() }),
        ...(smsChanged && !form.sms_opt_in && { sms_opt_out_at: new Date().toISOString() }),
      })
      .eq('id', profile.id);

    if (!error) {
      await fetchProfile(user.id);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
    setSaving(false);
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  return (
    <div className="flex-1 bg-primary">
      <div className="max-w-[500px] mx-auto px-4 md:px-7 py-8">
        <h1 className="text-[22px] font-bold text-primary-c mb-6">My Profile</h1>

        <div className="bg-elevated border border-theme rounded-xl p-6 shadow-sm">
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-3.5">
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">First Name</label>
                <input
                  type="text"
                  className={inputClass}
                  value={form.first_name}
                  onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">Last Name</label>
                <input
                  type="text"
                  className={inputClass}
                  value={form.last_name}
                  onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Email</label>
              <input type="email" className={inputClass + ' opacity-60'} value={profile?.email || ''} readOnly />
              <p className="text-[11px] text-muted-c mt-1">Contact your provider to update your email</p>
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Phone</label>
              <input
                type="tel"
                className={inputClass}
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="(555) 555-5555"
              />
              <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.sms_opt_in}
                  onChange={e => setForm(p => ({ ...p, sms_opt_in: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-[#0DA7CA] cursor-pointer"
                />
                <span className="text-[12px] text-secondary-c leading-relaxed">
                  Text me appointment &amp; referral notifications.{' '}
                  <span className="text-muted-c">
                    You&apos;ll receive appointment confirmations, reminders and changes,
                    referral updates, and account notices from MediLink — typically
                    1&ndash;4 messages per appointment. Msg &amp; data rates may apply. Reply STOP to cancel, HELP
                    for help. Consent is not required to receive care. See{' '}
                    <a href="https://www.medilink.vip/sms-terms" target="_blank" rel="noreferrer" className="text-accent-light hover:underline">SMS Terms</a>
                    {' '}&amp;{' '}
                    <a href="https://www.medilink.vip/privacy" target="_blank" rel="noreferrer" className="text-accent-light hover:underline">Privacy Policy</a>.
                  </span>
                </span>
              </label>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-theme">
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              {saved && <span className="text-[12px] font-medium text-success">Saved!</span>}
            </div>
          </form>
        </div>

        {/* Text notification numbers (068): more than one mobile, each verified, each with its own choices */}
        <div className="bg-elevated border border-theme rounded-[14px] p-5 mb-5">
          <h2 className="text-[15px] font-bold text-primary-c mb-1">Text notifications</h2>
          <SmsNumbers
            events={PATIENT_EVENTS}
            intro="Add the mobile numbers you want appointment reminders and referral updates sent to. Each number is verified with a code. Texts never include medical details."
          />
        </div>

        {/* Provider info */}
        {currentOrg && (
          <div className="mt-8">
            <h2 className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-c mb-3">Your Provider</h2>
            <div className="bg-elevated border border-theme rounded-xl p-5">
              <div className="text-[15px] font-semibold text-primary-c mb-1">{currentOrg.name}</div>
              {currentOrg.phone && (
                <a href={`tel:${currentOrg.phone}`} className="block text-[13px] text-accent-light hover:underline">{currentOrg.phone}</a>
              )}
              {currentOrg.email && (
                <a href={`mailto:${currentOrg.email}`} className="block text-[13px] text-accent-light hover:underline mt-0.5">{currentOrg.email}</a>
              )}
              {currentOrg.address_line1 && (
                <div className="text-[12px] text-muted-c mt-2">
                  {currentOrg.address_line1}{currentOrg.city ? `, ${currentOrg.city}` : ''}{currentOrg.state ? ` ${currentOrg.state}` : ''} {currentOrg.zip}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
import SmsNumbers from '../notifications/SmsNumbers';
import { PATIENT_EVENTS } from '../../lib/notificationEvents';
